<script setup lang="ts">
import Modal from 'dashboard/components/Modal.vue';
import { ref, onBeforeUnmount, nextTick, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

interface AttachedFile {
  url: string;
  type: string;
  fileName: string;
}

interface Audience {
  attached_files?: AttachedFile[];
  [key: string]: any;
}

interface InteractiveOverride {
  mode: string;
  value: string;
}

interface FilterData {
  interactive_overrides?: {
    title?: InteractiveOverride;
    footer?: InteractiveOverride;
    mediaUrl?: InteractiveOverride;
    buttons?: InteractiveOverride;
  };
  [key: string]: any;
}

interface TemplateParams {
  name?: string;
  template_id?: number;
}

interface CampaignDataProps {
  campaignName: string;
  message: string;
  hasMessage: boolean;
  audience: Audience | null;
  campaignStatus: string;
  progressPercentage: number;
  totalItems: number;
  itemsSent: number;
  itemsFailed: number;
  scheduledAt: string;
  statistics: any;
  statusMessage: string;
  campaignId: number;
  scheduleId: number;
  filterData?: FilterData | null;
  templateParams?: TemplateParams | null;
}

interface Props {
  isOpen: boolean;
  campaignData: CampaignDataProps;
}

const props = defineProps<Props>();
const emit = defineEmits<{ close: [] }>();

const isComponentMounted = ref(true);

// --- Interactive helpers ---
const interactiveOverrides = computed(() => {
  return props.campaignData?.filterData?.interactive_overrides || null;
});

const isInteractive = computed(() => {
  return !!interactiveOverrides.value;
});

const interactiveTitle = computed(() => {
  return interactiveOverrides.value?.title?.value || null;
});

const interactiveFooter = computed(() => {
  return interactiveOverrides.value?.footer?.value || null;
});

const interactiveMediaUrl = computed(() => {
  return interactiveOverrides.value?.mediaUrl?.value || null;
});

const interactiveMediaFileName = computed(() => {
  if (!interactiveMediaUrl.value) return '';
  try {
    const path = new URL(interactiveMediaUrl.value, window.location.origin).pathname;
    return decodeURIComponent(path.split('/').pop() || 'arquivo');
  } catch {
    return 'arquivo';
  }
});

const isInteractiveMediaImage = computed(() => {
  const url = interactiveMediaUrl.value || '';
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url)
    || url.includes('/image/')
    || url.includes('active_storage');
});

const isInteractiveMediaVideo = computed(() => {
  const url = interactiveMediaUrl.value || '';
  return /\.(mp4|webm|mov|avi)(\?|$)/i.test(url) || url.includes('/video/');
});

const isInteractiveMediaAudio = computed(() => {
  const url = interactiveMediaUrl.value || '';
  return /\.(mp3|ogg|wav|opus|aac)(\?|$)/i.test(url) || url.includes('/audio/');
});

const templateName = computed(() => {
  return props.campaignData?.templateParams?.name || null;
});

// --- Audience helpers ---
const allLabels = useMapGetter('labels/getLabels');

const audienceLabels = computed(() => {
  return props.campaignData?.audience?.labels || [];
});

const hasAudience = computed(() => audienceLabels.value.length > 0);

const resolvedLabels = computed(() => {
  const storeLabels = allLabels.value || [];
  return audienceLabels.value.map((al: any) => {
    const found = storeLabels.find((l: any) => l.id === al.id);
    return {
      id: al.id,
      title: found?.title || `Label #${al.id}`,
      color: found?.color || '#6366f1',
    };
  });
});

// --- Media helpers ---
const inferMimeType = (name: string): string => {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
    gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml',
    mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
    mp3: 'audio/mpeg', ogg: 'audio/ogg', wav: 'audio/wav', opus: 'audio/opus',
  };
  return map[ext] || '';
};

const attachedFiles = computed((): AttachedFile[] => {
  const raw = props.campaignData?.audience?.attached_files || [];
  return raw.map((f: any) => ({
    url: f.url || f.download_url || '',
    fileName: f.fileName || f.name || '',
    type: f.type || inferMimeType(f.fileName || f.name || ''),
  }));
});

const isImageFile = (file: AttachedFile) => {
  if (!file.type) return false;
  return file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileName || '');
};

const isVideoFile = (file: AttachedFile) => {
  if (!file.type) return false;
  return file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.fileName || '');
};

const isAudioFile = (file: AttachedFile) => {
  if (!file.type) return false;
  return file.type.startsWith('audio/') || /\.(mp3|ogg|wav|opus)$/i.test(file.fileName || '');
};

const getFileIcon = (file: AttachedFile) => {
  if (isImageFile(file)) return 'i-lucide-image';
  if (isVideoFile(file)) return 'i-lucide-film';
  if (isAudioFile(file)) return 'i-lucide-music';
  if (file.type?.includes('pdf')) return 'i-lucide-file-text';
  return 'i-lucide-file';
};

// --- Handlers ---
const handleClose = async () => {
  if (!isComponentMounted.value) return;
  await nextTick();
  if (isComponentMounted.value) {
    emit('close');
  }
};

onBeforeUnmount(() => {
  isComponentMounted.value = false;
});
</script>

<template>
  <Modal
    v-if="isOpen && isComponentMounted"
    :show="isOpen"
    @close="handleClose"
    size="medium"
  >
    <div class="flex flex-col h-[85vh] bg-white dark:bg-n-slate-1">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-slate-200 dark:border-n-slate-6">
        <div class="flex items-center gap-4">
          <div class="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
            <span class="i-lucide-megaphone h-5 w-5 text-white"></span>
          </div>
          <div>
            <h2 class="text-xl font-semibold text-slate-900 dark:text-n-slate-12">
              {{ campaignData.campaignName }}
            </h2>
            <p class="text-sm text-slate-600 dark:text-n-slate-11 mt-1">
              Detalhes da campanha Baileys
            </p>
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-1 overflow-hidden">
        <div class="h-full overflow-y-auto">
          <div class="p-6 space-y-6">

            <!-- ==================== Preview ==================== -->
            <div>
              <div v-if="!campaignData.hasMessage && attachedFiles.length === 0 && !isInteractive" class="flex items-center justify-center min-h-[400px]">
                <div class="text-center">
                  <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 dark:bg-n-slate-3 flex items-center justify-center">
                    <span class="i-lucide-message-circle h-8 w-8 text-slate-400 dark:text-n-slate-9"></span>
                  </div>
                  <h3 class="text-lg font-semibold text-slate-900 dark:text-n-slate-12 mb-2">
                    Nenhuma mensagem configurada
                  </h3>
                  <p class="text-slate-600 dark:text-n-slate-11">
                    Esta campanha nao possui mensagem.
                  </p>
                </div>
              </div>

              <div v-else class="space-y-6">

                <!-- Template type badge -->
                <div v-if="templateName" class="flex items-center gap-2">
                  <span
                    :class="isInteractive
                      ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300 border-violet-200 dark:border-violet-700'
                      : 'bg-slate-100 text-slate-600 dark:bg-n-slate-3 dark:text-n-slate-11 border-slate-200 dark:border-n-slate-6'"
                    class="px-3 py-1 text-xs font-medium rounded-full border inline-flex items-center gap-1.5"
                  >
                    <span :class="isInteractive ? 'i-lucide-layout-list' : 'i-lucide-file-text'" class="h-3 w-3"></span>
                    {{ templateName }}
                  </span>
                  <span v-if="isInteractive" class="text-xs text-violet-600 dark:text-violet-400 font-medium">
                    Interativo
                  </span>
                </div>

                <!-- Message Preview (WhatsApp bubble style) -->
                <div v-if="campaignData.hasMessage || isInteractive" class="bg-slate-50 dark:bg-n-slate-2 rounded-2xl p-6 border border-slate-200 dark:border-n-slate-6">
                  <div class="flex items-center gap-2 mb-4">
                    <span class="i-lucide-smartphone h-4 w-4 text-slate-500 dark:text-n-slate-11"></span>
                    <span class="text-sm font-medium text-slate-600 dark:text-n-slate-11">
                      Como o cliente vera
                    </span>
                  </div>

                  <div class="bg-white dark:bg-n-slate-1 rounded-xl shadow-sm border border-slate-200 dark:border-n-slate-6 overflow-hidden">
                    <!-- Chat header -->
                    <div class="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-n-slate-6">
                      <div class="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <span class="i-lucide-building h-4 w-4 text-white"></span>
                      </div>
                      <div>
                        <div class="text-sm font-medium text-slate-900 dark:text-n-slate-12">Sua Empresa</div>
                        <div class="text-xs text-slate-500 dark:text-n-slate-11">Agora</div>
                      </div>
                    </div>

                    <!-- Interactive media (header image) -->
                    <div v-if="interactiveMediaUrl" class="bg-slate-100 dark:bg-n-slate-3">
                      <img
                        :src="interactiveMediaUrl"
                        alt="Media da campanha"
                        class="w-full max-h-56 object-cover"
                      />
                    </div>

                    <div class="p-4">
                      <!-- Interactive title -->
                      <div v-if="interactiveTitle" class="font-semibold text-slate-900 dark:text-n-slate-12 mb-2">
                        {{ interactiveTitle }}
                      </div>

                      <!-- Message body -->
                      <div v-if="campaignData.hasMessage" class="text-slate-900 dark:text-n-slate-12 leading-relaxed whitespace-pre-wrap">
                        {{ campaignData.message }}
                      </div>

                      <!-- Interactive footer -->
                      <div v-if="interactiveFooter" class="mt-3 pt-2 text-xs text-slate-500 dark:text-n-slate-11">
                        {{ interactiveFooter }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Attached files preview -->
                <div v-if="attachedFiles.length > 0" class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                      <span class="i-lucide-paperclip h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Arquivos Anexados</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">{{ attachedFiles.length }} arquivo(s)</p>
                    </div>
                  </div>

                  <div class="grid gap-3">
                    <div
                      v-for="(file, index) in attachedFiles"
                      :key="index"
                      class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6"
                    >
                      <!-- Image thumbnail -->
                      <div v-if="isImageFile(file)" class="space-y-3">
                        <img
                          :src="file.url"
                          :alt="file.fileName"
                          class="max-h-48 rounded-lg object-contain bg-white dark:bg-n-slate-1"
                        />
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-2 text-sm text-slate-600 dark:text-n-slate-11">
                            <span class="i-lucide-image h-4 w-4"></span>
                            <span class="truncate">{{ file.fileName }}</span>
                          </div>
                          <a :href="file.url" target="_blank" rel="noopener noreferrer" class="p-2 hover:bg-slate-100 dark:hover:bg-n-slate-3 rounded-lg transition-colors">
                            <span class="i-lucide-external-link h-4 w-4 text-slate-500"></span>
                          </a>
                        </div>
                      </div>

                      <!-- Video -->
                      <div v-else-if="isVideoFile(file)" class="space-y-3">
                        <video
                          :src="file.url"
                          controls
                          class="max-h-48 rounded-lg bg-black"
                        ></video>
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-2 text-sm text-slate-600 dark:text-n-slate-11">
                            <span class="i-lucide-film h-4 w-4"></span>
                            <span class="truncate">{{ file.fileName }}</span>
                          </div>
                          <a :href="file.url" target="_blank" rel="noopener noreferrer" class="p-2 hover:bg-slate-100 dark:hover:bg-n-slate-3 rounded-lg transition-colors">
                            <span class="i-lucide-external-link h-4 w-4 text-slate-500"></span>
                          </a>
                        </div>
                      </div>

                      <!-- Audio -->
                      <div v-else-if="isAudioFile(file)" class="space-y-3">
                        <audio :src="file.url" controls class="w-full"></audio>
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-2 text-sm text-slate-600 dark:text-n-slate-11">
                            <span class="i-lucide-music h-4 w-4"></span>
                            <span class="truncate">{{ file.fileName }}</span>
                          </div>
                          <a :href="file.url" target="_blank" rel="noopener noreferrer" class="p-2 hover:bg-slate-100 dark:hover:bg-n-slate-3 rounded-lg transition-colors">
                            <span class="i-lucide-external-link h-4 w-4 text-slate-500"></span>
                          </a>
                        </div>
                      </div>

                      <!-- Generic file -->
                      <div v-else class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-lg bg-slate-200 dark:bg-n-slate-3 flex items-center justify-center">
                          <span :class="[getFileIcon(file), 'h-5 w-5 text-slate-500 dark:text-n-slate-11']"></span>
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-slate-900 dark:text-n-slate-12 truncate">
                            {{ file.fileName }}
                          </p>
                          <p class="text-xs text-slate-500 dark:text-n-slate-11">{{ file.type }}</p>
                        </div>
                        <a
                          :href="file.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="p-2 hover:bg-slate-100 dark:hover:bg-n-slate-3 rounded-lg transition-colors"
                        >
                          <span class="i-lucide-external-link h-4 w-4 text-slate-500"></span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Interactive overrides detail -->
                <div v-if="isInteractive" class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                      <span class="i-lucide-layout-list h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Campos Interativos</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Configuracao do template interativo</p>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div v-if="interactiveOverrides?.title" class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Titulo</label>
                      <div class="mt-2 text-sm font-medium text-slate-900 dark:text-n-slate-12">
                        {{ interactiveOverrides.title.value }}
                      </div>
                      <div class="mt-1 text-xs text-slate-400 dark:text-n-slate-11">
                        Modo: {{ interactiveOverrides.title.mode === 'fixed' ? 'Fixo' : 'Coluna' }}
                      </div>
                    </div>

                    <div v-if="interactiveOverrides?.footer" class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Rodape</label>
                      <div class="mt-2 text-sm font-medium text-slate-900 dark:text-n-slate-12">
                        {{ interactiveOverrides.footer.value }}
                      </div>
                      <div class="mt-1 text-xs text-slate-400 dark:text-n-slate-11">
                        Modo: {{ interactiveOverrides.footer.mode === 'fixed' ? 'Fixo' : 'Coluna' }}
                      </div>
                    </div>

                    <div v-if="interactiveOverrides?.mediaUrl" class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Media</label>
                      <div class="mt-3 space-y-3">
                        <!-- Image preview -->
                        <img
                          v-if="isInteractiveMediaImage"
                          :src="interactiveMediaUrl"
                          alt="Media interativa"
                          class="max-h-48 rounded-lg object-contain bg-white dark:bg-n-slate-1"
                          @error="($event.target as HTMLImageElement).style.display = 'none'"
                        />
                        <!-- Video preview -->
                        <video
                          v-else-if="isInteractiveMediaVideo"
                          :src="interactiveMediaUrl"
                          controls
                          class="max-h-48 rounded-lg bg-black"
                        />
                        <!-- Audio preview -->
                        <audio
                          v-else-if="isInteractiveMediaAudio"
                          :src="interactiveMediaUrl"
                          controls
                          class="w-full"
                        />
                        <!-- Generic file icon -->
                        <div v-else class="flex items-center gap-2">
                          <div class="w-10 h-10 rounded-lg bg-slate-200 dark:bg-n-slate-3 flex items-center justify-center">
                            <span class="i-lucide-file h-5 w-5 text-slate-500 dark:text-n-slate-11" />
                          </div>
                          <span class="text-sm text-slate-600 dark:text-n-slate-11 truncate">{{ interactiveMediaFileName }}</span>
                        </div>
                        <!-- Download link -->
                        <div class="flex items-center justify-between">
                          <span class="text-xs text-slate-400 dark:text-n-slate-11">
                            Modo: {{ interactiveOverrides.mediaUrl.mode === 'fixed' ? 'Fixo' : 'Coluna' }}
                          </span>
                          <a
                            :href="interactiveMediaUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            <span class="i-lucide-external-link h-3.5 w-3.5" />
                            Abrir
                          </a>
                        </div>
                      </div>
                    </div>

                    <div v-if="interactiveOverrides?.buttons" class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Botoes</label>
                      <div class="mt-2 text-sm font-medium text-slate-900 dark:text-n-slate-12">
                        {{ interactiveOverrides.buttons.value }}
                      </div>
                      <div class="mt-1 text-xs text-slate-400 dark:text-n-slate-11">
                        Modo: {{ interactiveOverrides.buttons.mode === 'fixed' ? 'Fixo' : 'Coluna' }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Audience labels (tag campaigns) -->
                <div v-if="hasAudience" class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                      <span class="i-lucide-tag h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Audiencia</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Labels usadas para segmentar contatos</p>
                    </div>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="label in resolvedLabels"
                      :key="label.id"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full border"
                      :style="{
                        backgroundColor: label.color + '1a',
                        color: label.color,
                        borderColor: label.color + '40',
                      }"
                    >
                      <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: label.color }"></span>
                      {{ label.title }}
                    </span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-slate-200 dark:border-n-slate-6 bg-white dark:bg-n-slate-1">
        <div class="flex justify-end">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-sm font-medium text-slate-700 dark:text-n-slate-11 hover:text-slate-900 dark:hover:text-n-slate-12 bg-slate-100 dark:bg-n-slate-3 hover:bg-slate-200 dark:hover:bg-n-slate-4 rounded-lg transition-colors duration-200 border border-slate-300 dark:border-n-slate-6"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184);
}

.dark .overflow-y-auto {
  scrollbar-color: rgb(var(--slate-6)) transparent;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgb(var(--slate-6));
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgb(var(--slate-7));
}

/* Spinner animation */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Focus states */
button:focus-visible {
  @apply ring-2 ring-green-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900;
}
</style>
